/**
 * Inngest event payloads.
 *
 * Architecture §7.2: dotted, past-tense, namespaced by bounded context. Names
 * are the wire format; the schema is the contract. New events get a Zod schema
 * here, a literal name, and an entry in the `Events` discriminated union.
 *
 * §15 rule 8: every Inngest function declares retries / concurrency / rateLimit
 * — that's enforced in `apps/api/src/inngest/`, not here.
 */
import { z } from "zod";
import { uuid, moneyPaise, isoDate } from "../_common.js";

export const invoiceIssued = z.object({
  name: z.literal("invoice.issued"),
  data: z.object({
    invoiceId: uuid,
    companyId: uuid,
    customerId: uuid,
    docNumber: z.string(),
    totalPaise: moneyPaise,
    requiresEInvoice: z.boolean(),
  }),
});

export const invoiceVoided = z.object({
  name: z.literal("invoice.voided"),
  data: z.object({
    invoiceId: uuid,
    companyId: uuid,
    reason: z.string(),
    replacementId: uuid.optional(),
  }),
});

/**
 * Proforma finalized (ADR-0032) — a draft proforma was numbered (PRO/…) and
 * frozen. NON-FISCAL: no ledger, no receivable. No V1 reactor consumes it yet
 * (a future handler could render + send the proforma PDF); the schema exists
 * so the seam is observable, mirroring the `purchase-order.sent` precedent.
 */
export const proformaFinalized = z.object({
  name: z.literal("proforma.finalized"),
  data: z.object({
    proformaId: uuid,
    companyId: uuid,
    customerId: uuid,
    docNumber: z.string(),
    totalPaise: moneyPaise,
  }),
});

/**
 * Proforma converted (ADR-0032) — a finalized proforma produced a real tax
 * invoice (`convertToInvoice`). Fired alongside `invoice.issued`; carries the
 * lineage both sides. Observability seam — no V1 reactor yet.
 */
export const proformaConverted = z.object({
  name: z.literal("proforma.converted"),
  data: z.object({
    proformaId: uuid,
    companyId: uuid,
    invoiceId: uuid,
    invoiceDocNumber: z.string(),
  }),
});

export const paymentRecorded = z.object({
  name: z.literal("payment.recorded"),
  data: z.object({
    receiptId: uuid,
    companyId: uuid,
    customerId: uuid,
    amountPaise: moneyPaise,
  }),
});

export const billExtracted = z.object({
  name: z.literal("bill.extracted"),
  data: z.object({
    billId: uuid,
    companyId: uuid,
    overallConfidence: z.number().min(0).max(1),
  }),
});

export const billMatched = z.object({
  name: z.literal("bill.matched"),
  data: z.object({
    billId: uuid,
    companyId: uuid,
    threeWayMatch: z.enum(["matched", "mismatch", "na"]),
  }),
});

export const briefingGenerated = z.object({
  name: z.literal("briefing.generated"),
  data: z.object({
    companyId: uuid,
    forDate: z.string(),
    promptId: uuid,
    costPaise: moneyPaise,
  }),
});

/**
 * A buyer placed an order on the storefront. Audience: **staff** — the
 * consumer fans out to everyone holding `store.orders.approve` (ADR-0064).
 *
 * Payload is deliberately minimal: the consumer re-reads the order row rather
 * than trusting the event (ADR-0018's Trustful Event anti-pattern — events are
 * notifications, the row is truth).
 */
export const storeOrderPlaced = z.object({
  name: z.literal("store.order.placed"),
  data: z.object({
    orderId: uuid,
    companyId: uuid,
    storeId: uuid,
  }),
});

/**
 * A storefront order moved between statuses. Audience: **buyer**. One event
 * covers `confirmed` / `approved` / `fulfilled` / `rejected` and any status
 * added later; the consumer maps `to` → template (ADR-0064).
 *
 * `from`/`to` are the pair `transitionOrderStatus` has already computed. The
 * idempotent no-op (asking for the status an order already holds) returns no
 * event at all, so this never fires with `from === to`.
 */
export const storeOrderStatusChanged = z.object({
  name: z.literal("store.order.status-changed"),
  data: z.object({
    orderId: uuid,
    companyId: uuid,
    from: z.enum(["draft", "confirmed", "approved", "fulfilled", "rejected"]),
    to: z.enum(["draft", "confirmed", "approved", "fulfilled", "rejected"]),
  }),
});

export const notificationQueued = z.object({
  name: z.literal("notification.queued"),
  data: z.object({
    notificationId: uuid,
    companyId: uuid,
    channel: z.enum(["whatsapp", "sms", "email", "in-app"]),
    priority: z.enum(["critical", "important", "informational"]),
  }),
});

export const notificationHeld = z.object({
  name: z.literal("notification.held"),
  data: z.object({
    notificationId: uuid,
    companyId: uuid,
    /** Which guardrail held the send (system-decisions §4). */
    reason: z.enum([
      "notifications-disabled",
      "freshness",
      "recent-conversation",
      "rate-limit",
      "confidence-floor",
      "quiet-hours",
    ]),
  }),
});

/** Adapter accepted the message. Vendor id (when returned) lets future
 *  delivery-receipt handlers correlate. */
export const notificationSent = z.object({
  name: z.literal("notification.sent"),
  data: z.object({
    notificationId: uuid,
    companyId: uuid,
    channel: z.enum(["whatsapp", "sms", "email", "in-app"]),
    /** Provider's id (e.g. Meta wamid, Resend id). Null for dev-stub sends. */
    vendorMessageId: z.string().nullable(),
  }),
});

/** Adapter exhausted retries and the message is permanently failed. The
 *  Inngest function emits this only AFTER its own retry policy gave up. */
export const notificationFailed = z.object({
  name: z.literal("notification.failed"),
  data: z.object({
    notificationId: uuid,
    companyId: uuid,
    channel: z.enum(["whatsapp", "sms", "email", "in-app"]),
    /** Truncated last-attempt error message. Sentry has the stack. */
    error: z.string(),
  }),
});

/**
 * Vendor payment recorded — fired after a `vendor_payments` row commits with
 * `status='paid'` (either below-threshold or via approvalId path). Carries
 * the audit-safe summary; no bank account numbers, IFSCs, or counterparty PII.
 *
 * V1.0 has no reactor consuming this event (TDS aggregation lands with
 * `money/tds/`; vendor remittance email lands with `notifications/email/`).
 * The schema exists now so the seam is observable and reactors don't
 * back-fit the producer.
 */
export const vendorPaymentRecorded = z.object({
  name: z.literal("vendor-payment.recorded"),
  data: z.object({
    vendorPaymentId: uuid,
    companyId: uuid,
    vendorId: uuid,
    grossPaise: moneyPaise,
    tdsPaise: moneyPaise,
    netPaise: moneyPaise,
    billIds: z.array(uuid),
    /** Section the TDS was deducted under, if any. `null` when no TDS. */
    tdsSection: z.string().nullable(),
  }),
});

/**
 * Vendor payment queued for founder approval — operator submitted a payment
 * ≥ threshold, but no `vendor_payments` row exists yet (RLS is INSERT-only).
 * The `requestPayload` lives on the `pending_approvals` row and is the source
 * of truth for the eventual execution.
 */
export const vendorPaymentAwaitingApproval = z.object({
  name: z.literal("vendor-payment.awaiting-approval"),
  data: z.object({
    approvalId: uuid,
    companyId: uuid,
    vendorId: uuid,
    grossPaise: moneyPaise,
    billIds: z.array(uuid),
  }),
});

/**
 * Vendor payment voided — a *new* `vendor_payments` row was inserted with
 * `status='void'` and `voids_id` pointing at the original. The original row
 * is unchanged (append-only). Bill statuses recompute backward.
 */
export const vendorPaymentVoided = z.object({
  name: z.literal("vendor-payment.voided"),
  data: z.object({
    reversingVendorPaymentId: uuid,
    originalVendorPaymentId: uuid,
    companyId: uuid,
    vendorId: uuid,
    reason: z.string(),
  }),
});

/**
 * Purchase order sent — fired after `purchaseOrders.send` allocates the
 * gap-allowed number and flips draft → sent. No V1.0 reactor consumes it yet
 * (PO PDF render + vendor notification land in a follow-up); the schema exists
 * so the seam is observable. Mirrors the `vendor-payment.recorded` precedent.
 */
export const purchaseOrderSent = z.object({
  name: z.literal("purchase-order.sent"),
  data: z.object({
    purchaseOrderId: uuid,
    companyId: uuid,
    vendorId: uuid,
    poNumber: z.string(),
    totalPaise: moneyPaise,
  }),
});

/**
 * Goods receipt posted — fired after `goodsReceipts.record` commits (PO status
 * flipped + draft matching bill created in the same tx). No V1.0 reactor yet;
 * the schema exists so the seam is observable.
 */
export const goodsReceiptPosted = z.object({
  name: z.literal("goods-receipt.posted"),
  data: z.object({
    goodsReceiptId: uuid,
    companyId: uuid,
    purchaseOrderId: uuid,
    vendorId: uuid,
    grnNumber: z.string(),
    acceptedValuePaise: moneyPaise,
    billId: uuid.nullable(),
  }),
});

export const whatsappCreditDepleted = z.object({
  name: z.literal("whatsapp-credit.depleted"),
  data: z.object({
    companyId: uuid,
    threshold: z.enum(["80%", "100%"]),
  }),
});

/**
 * AI usage recorded — emitted by an AI surface (web chat, bot) AFTER a turn
 * succeeds, carrying the actual model + token counts. The credit metering engine
 * (`ai-usage-recorded` background job → `meterAiUsage`) prices it (tokens ×
 * model price) and debits the AI wallet with a DETAILED transaction. This keeps
 * pricing/billing OUT of the AI surfaces: they just report what happened. A
 * metering failure never breaks the delivered answer, and a replay re-runs
 * idempotently per `idempotencyKey`.
 */
export const aiUsageRecorded = z.object({
  name: z.literal("ai.usage.recorded"),
  data: z.object({
    companyId: uuid,
    // Mirrors domain AiQueryType (api-contract can't import domain) — keep in sync.
    queryType: z.enum([
      "chat_message",
      "briefing_generation",
      "bill_extraction",
      "document_narration",
    ]),
    /** Actual provider/OpenRouter model id that ran (priced via resolveModelKey). */
    model: z.string().nullable(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    /** Dedupe key — a replay of the same turn never double-charges. */
    idempotencyKey: z.string().min(1),
    /** Provenance for the detailed usage statement. */
    surface: z.enum(["web", "bot", "scheduled"]).optional(),
    threadId: uuid.optional(),
    platform: z.string().optional(),
  }),
});

/** Workspace created — fired after `identity.createWorkspace` commits. */
export const workspaceCreated = z.object({
  name: z.literal("workspace.created"),
  data: z.object({
    companyId: uuid,
    founderUserId: uuid,
    plan: z.enum(["starter", "growth", "scale", "design_partner"]),
    trialEndsOn: isoDate,
  }),
});

/**
 * Subscription plan changed — fired after `subscriptionBilling.changePlan`
 * commits (plan re-pointed + entitlements recomputed + audit row). No V1.0
 * reactor consumes it yet (a future handler could re-point a Razorpay mandate
 * or send a confirmation); the schema exists so the seam is observable, mirroring
 * the `workspace.created` precedent.
 */
export const subscriptionPlanChanged = z.object({
  name: z.literal("subscription.plan-changed"),
  data: z.object({
    companyId: uuid,
    subscriptionId: uuid,
    fromPlan: z.enum(["starter", "growth", "scale", "design_partner"]),
    toPlan: z.enum(["starter", "growth", "scale", "design_partner"]),
    direction: z.enum(["upgrade", "downgrade", "lateral"]),
    changedByUserId: uuid,
  }),
});

/**
 * Add-on installed/removed — fired after `subscriptionBilling.setAddon` commits
 * (row upserted + entitlements recomputed + audit). Observability seam; a future
 * handler could re-point add-on billing. Mirrors `subscription.plan-changed`.
 */
export const subscriptionAddonChanged = z.object({
  name: z.literal("subscription.addon-changed"),
  data: z.object({
    companyId: uuid,
    subscriptionId: uuid,
    addonKey: z.string(),
    enabled: z.boolean(),
    changedByUserId: uuid,
  }),
});

/**
 * Subscription payment failed (dunning D0) — fired from the Razorpay webhook
 * when a recurring charge fails (`subscription.halted`/`pending`) right after
 * the row is marked `past_due`. The `subscription-past-due-notify` reactor tells
 * the workspace owner(s) so they can fix the payment method before the D7
 * read-only suspension (system-decisions §15). Idempotent downstream per
 * (company, billing month) so a re-sent webhook can't double-notify.
 */
export const subscriptionPastDue = z.object({
  name: z.literal("subscription.past-due"),
  data: z.object({
    companyId: uuid,
    subscriptionId: uuid,
    /** Which Razorpay event drove the transition — for copy/telemetry only. */
    reason: z.enum(["halted", "pending"]),
  }),
});

/** Tally / Zoho / QuickBooks intent captured during onboarding (S04). */
export const migrationIntentCaptured = z.object({
  name: z.literal("migration-intent.captured"),
  data: z.object({
    migrationIntentId: uuid,
    companyId: uuid,
    source: z.enum(["tally", "zoho", "quickbooks", "none"]),
    deferred: z.boolean(),
  }),
});

/**
 * Approval requested — fired after `requestApproval` inserts the
 * `pending_approvals` row + audit entry. Consumed by the WhatsApp
 * approval Inngest function which renders the per-kind template + sends
 * it to the founder's phone with Approve / Reject quick-reply buttons.
 *
 * The audit/observability seam is the producer side (the same shape every
 * Flint event has); the rest of the work — render, dispatch, retry —
 * lives in the consumer where retry policy can be tuned independently.
 *
 * `summary` is the human-readable card title that already lives on the
 * approval row (e.g. "Pay Saffron Dyes & Chemicals ₹76,700"). Keeping
 * it on the event lets the consumer render the template without re-
 * reading the row (saves a tenant-scoped DB round-trip).
 */
export const approvalRequested = z.object({
  name: z.literal("approval.requested"),
  data: z.object({
    approvalId: uuid,
    companyId: uuid,
    /**
     * The action type / legacy kind. Free-form string since ADR-0031 made `kind`
     * the gateable action type (`vendor_payment`, `purchase_order`, …) on top of
     * the legacy kinds; the consumer only uses it for template selection.
     */
    kind: z.string(),
    summary: z.string(),
    entityType: z.string(),
    entityId: uuid,
    /**
     * ADR-0031 — the resolved current-stage approver user-ids to ping. Empty for
     * legacy founder-only approvals (the consumer falls back to all founders).
     */
    approverUserIds: z.array(uuid).default([]),
    /** When false, keep the in-app approval active but suppress push delivery. */
    notifyApprovers: z.boolean().default(true),
  }),
});

/**
 * Approval stage advanced (ADR-0031) — a multi-stage chain cleared one stage and
 * the next is now active. The WhatsApp consumer pings the next stage's approvers.
 */
export const approvalStageAdvanced = z.object({
  name: z.literal("approval.stage-advanced"),
  data: z.object({
    approvalId: uuid,
    companyId: uuid,
    summary: z.string(),
    /** Zero-based index of the now-active stage. */
    stage: z.number().int().nonnegative(),
    approverUserIds: z.array(uuid),
    /** When false, keep the in-app approval active but suppress push delivery. */
    notifyApprovers: z.boolean().default(true),
  }),
});

/**
 * Approval decided (ADR-0031) — the chain reached a terminal state. Observability
 * + the seam future per-context re-execute handlers (ADR-0018) listen on. The
 * milestone's vendor-payment / payroll flows execute via operator resubmit, so
 * this event has no consumer yet; it's emitted for the audit/observability seam.
 */
export const approvalDecided = z.object({
  name: z.literal("approval.decided"),
  data: z.object({
    approvalId: uuid,
    companyId: uuid,
    actionType: z.string(),
    decision: z.enum(["approved", "rejected"]),
    decidedByUserId: uuid,
    via: z.enum(["web", "whatsapp"]),
    entityType: z.string(),
    entityId: uuid,
  }),
});

/**
 * OTP requested — emitted by `identity.requestOtp` after the challenge row
 * lands in `otp_verifications`. The router dispatches the SMS send
 * synchronously (the user is waiting on the result), but emitting an event
 * keeps the audit/observability seam consistent and lets us promote delivery
 * to an Inngest function later without changing the domain function.
 *
 * `otp` is the plaintext 6-digit code. It is intended ONLY for the dev
 * stub adapter (logs it) and the SMS adapter (sends it). It must never land
 * in audit, must never land in Sentry, and must never persist on the event
 * for replay. The field is named `otp` because that key is already in the
 * Sentry PII deny-list (`packages/config/src/sentry/deny-list.ts`) — even if
 * an unhandled error captures the event payload, `beforeSend` strips it.
 */
export const notificationOtpRequested = z.object({
  name: z.literal("notification.otp-requested"),
  data: z.object({
    challengeId: uuid,
    phone: z.string(),
    channel: z.enum(["sms", "whatsapp"]),
    /** Plaintext 6-digit code — see redaction note above. */
    otp: z.string().regex(/^\d{6}$/),
    expiresAt: z.string(),
  }),
});

/**
 * Email OTP requested — emitted by `identity.requestEmailVerifyOtp` after
 * the challenge row lands. The router dispatches the email send via Resend
 * synchronously (the user is waiting). Same PII redaction rules as the
 * phone OTP event above: `otp` is in Sentry's deny-list.
 */
export const notificationEmailOtpRequested = z.object({
  name: z.literal("notification.email-otp-requested"),
  data: z.object({
    challengeId: uuid,
    email: z.string().email(),
    channel: z.literal("email"),
    /** Plaintext 6-digit code — see redaction note on phone OTP event. */
    otp: z.string().regex(/^\d{6}$/),
    expiresAt: z.string(),
  }),
});

/**
 * An AI scheduled job needs to run. The runner sleeps until `runAtIso`
 * (durably, via `step.sleepUntil`) then fires it — no polling cron. A matching
 * `ai.scheduled-job.cancelled` (same `jobId`) cancels the sleeping run.
 */
export const aiScheduledJobDue = z.object({
  name: z.literal("ai.scheduled-job.due"),
  data: z.object({
    jobId: uuid,
    companyId: uuid,
    userId: uuid,
    /** When the job should fire (ISO-8601 UTC). The runner sleeps until this. */
    runAtIso: z.string(),
  }),
});

/** Cancels a sleeping scheduled-job runner (delete / reschedule). */
export const aiScheduledJobCancelled = z.object({
  name: z.literal("ai.scheduled-job.cancelled"),
  data: z.object({ jobId: uuid }),
});

/**
 * An inbound message from Telegram / Slack / WhatsApp that should be
 * processed by the Flint AI agent and replied to on the same platform.
 * Webhook handlers emit this after signature verification; the Inngest
 * function resolves the bot session, runs the agent, and sends the reply.
 */
export const agentBotTurnRequested = z.object({
  name: z.literal("agent.bot-turn.requested"),
  data: z.object({
    platform: z.enum(["whatsapp", "slack", "telegram"]),
    /** Platform-specific chat identifier (WA E.164 phone, Slack channel id, Telegram chat_id). */
    platformChatId: z.string(),
    /** Platform-specific sender identifier (WA phone, Slack user id, Telegram user id). */
    platformUserId: z.string(),
    text: z.string(),
    /** Platform's own message id — used as idempotency key end-to-end. */
    idempotencyKey: z.string(),
    /** Telegram voice note file_id — present instead of text for voice messages. */
    voiceFileId: z.string().optional(),
    /** Telegram photo file_id (largest size) — present when user sends an image. */
    photoFileId: z.string().optional(),
    /** Telegram document file_id — present when user sends a PDF or other document. */
    documentFileId: z.string().optional(),
    documentMimeType: z.string().optional(),
    documentFileName: z.string().optional(),
  }),
});

/**
 * A document was marked as knowledge base — extract its text, embed it, and
 * store the chunks for agent search. Idempotent: re-running replaces chunks.
 */
export const kbDocumentIndexRequested = z.object({
  name: z.literal("kb.document.index-requested"),
  data: z.object({
    documentId: uuid,
    companyId: uuid,
  }),
});

/** A retail counter sale was rung up (issued + paid + stock-decremented in one
 *  tx). Carries telemetry for receipt print / loyalty / dashboard cache-bust. */
export const posSaleRung = z.object({
  name: z.literal("pos.sale.rung"),
  data: z.object({
    invoiceId: uuid,
    companyId: uuid,
    customerId: uuid,
    docNumber: z.string(),
    grandTotalPaise: moneyPaise,
    tenderCount: z.number().int().positive(),
    changePaise: moneyPaise,
  }),
});

export const Events = z.discriminatedUnion("name", [
  aiScheduledJobDue,
  aiScheduledJobCancelled,
  agentBotTurnRequested,
  kbDocumentIndexRequested,
  invoiceIssued,
  invoiceVoided,
  proformaFinalized,
  proformaConverted,
  paymentRecorded,
  vendorPaymentRecorded,
  vendorPaymentAwaitingApproval,
  vendorPaymentVoided,
  purchaseOrderSent,
  goodsReceiptPosted,
  billExtracted,
  billMatched,
  briefingGenerated,
  storeOrderPlaced,
  storeOrderStatusChanged,
  notificationQueued,
  notificationHeld,
  notificationSent,
  notificationFailed,
  whatsappCreditDepleted,
  aiUsageRecorded,
  workspaceCreated,
  subscriptionPlanChanged,
  subscriptionAddonChanged,
  subscriptionPastDue,
  migrationIntentCaptured,
  notificationOtpRequested,
  notificationEmailOtpRequested,
  approvalRequested,
  approvalStageAdvanced,
  approvalDecided,
  posSaleRung,
]);

export type FlintEvent = z.infer<typeof Events>;
export type FlintEventName = FlintEvent["name"];
